<template>
  <div class="punish-page">

    <!-- ================= 处罚指标 ================= -->
    <el-card class="block-card" shadow="never">
      <div class="block-header">
        <span class="block-title">处罚指标</span>

        <el-popover placement="bottom-end" width="420" trigger="click">
          <el-table :data="summaryColumns" size="mini" border>
            <el-table-column label="显示" width="60">
              <template slot-scope="scope">
                <el-checkbox v-model="scope.row.show" />
              </template>
            </el-table-column>
            <el-table-column prop="label" label="列名" />
            <el-table-column label="固定" width="140">
              <template slot-scope="scope">
                <el-select v-model="scope.row.fixed" size="mini" clearable>
                  <el-option label="不固定" :value="false" />
                  <el-option label="左固定" value="left" />
                  <el-option label="右固定" value="right" />
                </el-select>
              </template>
            </el-table-column>
          </el-table>

          <el-button slot="reference" size="mini" icon="el-icon-setting">列设置</el-button>
        </el-popover>
      </div>

      <el-form :inline="true" :model="summaryQuery" size="small" class="filter-form">
        <el-form-item label="处罚周期">
          <el-date-picker
            v-model="summaryPartitionRange"
            type="monthrange"
            value-format="yyyyMM"
            start-placeholder="开始周期"
            end-placeholder="结束周期"
            clearable
          />
        </el-form-item>

        <el-form-item label="客户名称">
          <el-input
            v-model="summaryQuery.ops_advertiser_name"
            clearable
            :disabled="advertiserNameLocked && !summaryQuery.ops_advertiser_name"
          />
        </el-form-item>

        <el-form-item label="代理商集团">
          <el-input
            v-model="summaryQuery.zc_agent_group"
            clearable
            :disabled="agentGroupLocked && !summaryQuery.zc_agent_group"
          />
        </el-form-item>

        <el-form-item label="一级行业">
          <el-input v-model="summaryQuery.first_level_industry_name" clearable />
        </el-form-item>

        <el-form-item label="二级行业">
          <el-input v-model="summaryQuery.second_level_industry_name" clearable />
        </el-form-item>

        <el-form-item label="是否处罚">
          <el-select v-model="summaryQuery.is_penalized" clearable>
            <el-option label="是" :value="1" />
            <el-option label="否" :value="0" />
          </el-select>
        </el-form-item>

        <el-form-item>
          <el-button type="primary" @click="fetchSummary(true)">查询</el-button>
          <el-button @click="resetSummary">重置</el-button>
        </el-form-item>
      </el-form>

      <el-table
        :key="summaryTableKey"
        :data="summaryList"
        border
        size="small"
        style="margin-top: 12px"
      >
        <el-table-column
          v-for="col in visibleSummaryColumns"
          :key="col.prop + '_' + (col.fixed || 'none')"
          :prop="col.prop"
          :label="col.label"
          :width="col.width"
          :fixed="col.fixed || false"
          show-overflow-tooltip
        >
          <template slot-scope="scope">
            <div
              class="cell-copy"
              :title="getCellCopyText(scope.row, col.prop)"
              @click.stop="copyCell(getCellCopyText(scope.row, col.prop), col.label)"
            >
              <template v-if="col.prop === 'is_penalized'">
                <el-tag v-if="scope.row.is_penalized === 1" type="danger">是</el-tag>
                <el-tag v-else type="success">否</el-tag>
              </template>
              <template v-else>
                {{ scope.row[col.prop] }}
              </template>
            </div>
          </template>
        </el-table-column>
      </el-table>

      <el-pagination
        background
        layout="total, sizes, prev, pager, next"
        :total="summaryTotal"
        :current-page.sync="summaryQuery.page"
        :page-size="summaryQuery.page_size"
        :page-sizes="[10, 20, 50, 100, 200]"
        style="margin-top: 12px; text-align: right"
        @current-change="onSummaryPageChange"
        @size-change="onSummarySizeChange"
      />
    </el-card>

    <!-- ================= 处罚详情 ================= -->
    <el-card class="block-card" shadow="never">
      <div class="block-header">
        <span class="block-title">处罚详情</span>

        <el-popover placement="bottom-end" width="420" trigger="click">
          <el-table :data="detailColumns" size="mini" border>
            <el-table-column label="显示" width="60">
              <template slot-scope="scope">
                <el-checkbox v-model="scope.row.show" />
              </template>
            </el-table-column>
            <el-table-column prop="label" label="列名" />
            <el-table-column label="固定" width="140">
              <template slot-scope="scope">
                <el-select v-model="scope.row.fixed" size="mini" clearable>
                  <el-option label="不固定" :value="false" />
                  <el-option label="左固定" value="left" />
                  <el-option label="右固定" value="right" />
                </el-select>
              </template>
            </el-table-column>
          </el-table>

          <el-button slot="reference" size="mini" icon="el-icon-setting">列设置</el-button>
        </el-popover>
      </div>

      <el-form :inline="true" :model="detailQuery" size="small" class="filter-form">
        <el-form-item label="统计周期">
          <el-date-picker
            v-model="detailPartitionRange"
            type="monthrange"
            value-format="yyyyMM"
            clearable
          />
        </el-form-item>

        <el-form-item label="最后审核时间">
          <el-date-picker
            v-model="detailReviewTimeRange"
            type="datetimerange"
            value-format="yyyy-MM-dd HH:mm:ss"
            clearable
          />
        </el-form-item>

        <el-form-item label="广告主主体名称">
          <el-input
            v-model="detailQuery.ops_advertiser_name"
            clearable
            :disabled="advertiserNameLocked && !detailQuery.ops_advertiser_name"
          />
        </el-form-item>

        <el-form-item label="代理商ID">
          <el-input v-model="detailQuery.agency_uid" clearable />
        </el-form-item>

        <el-form-item label="代理商名称">
          <el-input v-model="detailQuery.agency_name" clearable />
        </el-form-item>

        <el-form-item label="代理商政策集团">
          <el-input
            v-model="detailQuery.zc_agent_group"
            clearable
            :disabled="agentGroupLocked && !detailQuery.zc_agent_group"
          />
        </el-form-item>

        <el-form-item label="账户K框">
          <el-input v-model="detailQuery.account_frame" clearable />
        </el-form-item>

        <el-form-item label="开户一级行业">
          <el-input v-model="detailQuery.first_level_industry_name" clearable />
        </el-form-item>

        <el-form-item label="开户二级行业">
          <el-input v-model="detailQuery.second_level_industry_name" clearable />
        </el-form-item>

        <el-form-item label="UID">
          <el-input v-model="detailQuery.account_id" clearable />
        </el-form-item>

        <el-form-item label="AID">
          <el-input v-model="detailQuery.adgroup_id" clearable />
        </el-form-item>

        <el-form-item label="DCID">
          <el-input v-model="detailQuery.dynamic_creative_id" clearable />
        </el-form-item>

        <el-form-item label="组件ID">
          <el-input v-model="detailQuery.component_id" clearable />
        </el-form-item>

        <el-form-item label="元素类型">
          <el-input v-model="detailQuery.element_type" clearable />
        </el-form-item>

        <el-form-item label="元素值">
          <el-input v-model="detailQuery.element_value" clearable />
        </el-form-item>

        <el-form-item label="元素指纹">
          <el-input v-model="detailQuery.physical_finger" clearable />
        </el-form-item>

        <el-form-item label="拒绝原因">
          <el-input v-model="detailQuery.reject_reason" clearable />
        </el-form-item>

        <el-form-item label="是否有申诉记录">
          <el-select v-model="detailQuery.is_element_appeal" clearable>
            <el-option label="是" :value="1" />
            <el-option label="否" :value="0" />
          </el-select>
        </el-form-item>

        <el-form-item>
          <el-button type="primary" @click="fetchDetail(true)">查询</el-button>
          <el-button @click="resetDetail">重置</el-button>
        </el-form-item>
      </el-form>

      <el-table
        :key="detailTableKey"
        :data="detailList"
        border
        size="small"
        style="margin-top: 12px"
      >
        <el-table-column
          v-for="col in visibleDetailColumns"
          :key="col.prop + '_' + (col.fixed || 'none')"
          :prop="col.prop"
          :label="col.label"
          :width="col.width"
          :fixed="col.fixed || false"
          show-overflow-tooltip
        >
          <template slot-scope="scope">
            <div
              class="cell-copy"
              :title="getCellCopyText(scope.row, col.prop)"
              @click.stop="copyCell(getCellCopyText(scope.row, col.prop), col.label)"
            >
              <template v-if="col.prop === 'element_type'">
                {{ ELEMENT_TYPE_MAP[scope.row.element_type] || scope.row.element_type }}
              </template>

              <template v-else-if="col.prop === 'is_element_appeal'">
                <el-tag v-if="scope.row.is_element_appeal === 1" type="danger">是</el-tag>
                <el-tag v-else type="success">否</el-tag>
              </template>

              <template v-else>
                {{ scope.row[col.prop] }}
              </template>
            </div>
          </template>
        </el-table-column>
      </el-table>

      <el-pagination
        background
        layout="total, sizes, prev, pager, next"
        :total="detailTotal"
        :current-page.sync="detailQuery.page"
        :page-size="detailQuery.page_size"
        :page-sizes="[10, 20, 50, 100, 200]"
        style="margin-top: 12px; text-align: right"
        @current-change="onDetailPageChange"
        @size-change="onDetailSizeChange"
      />
    </el-card>

  </div>
</template>

<script>
import { measurementIndicator, measurementDetail } from '@/api/releaseGroup.js'

export default {
  name: 'Billmeasure',
  data() {
    return {
      summaryTableKey: 0,
      detailTableKey: 0,

      summaryQuery: {
        partition_time_start: '',
        partition_time_end: '',
        ops_advertiser_name: '',
        zc_agent_group: '',
        first_level_industry_name: '',
        second_level_industry_name: '',
        is_penalized: '',
        page: 1,
        page_size: 10
      },
      detailQuery: {
        partition_time_start: '',
        partition_time_end: '',
        last_review_time_start: '',
        last_review_time_end: '',
        ops_advertiser_name: '',
        agency_uid: '',
        agency_name: '',
        zc_agent_group: '',
        account_frame: '',
        first_level_industry_name: '',
        second_level_industry_name: '',
        account_id: '',
        adgroup_id: '',
        dynamic_creative_id: '',
        component_id: '',
        element_type: '',
        element_value: '',
        physical_finger: '',
        reject_reason: '',
        is_element_appeal: '',
        page: 1,
        page_size: 10
      },

      ELEMENT_TYPE_MAP: {
        ELEMENT_TYPE_VIDEO: '视频',
        ELEMENT_TYPE_URL: '落地页',
        ELEMENT_TYPE_IMAGE: '图片',
        ELEMENT_TYPE_TEXT: '文字'
      },

      summaryList: [],
      detailList: [],
      summaryTotal: 0,
      detailTotal: 0,
      summaryPartitionRange: [],
      detailPartitionRange: [],
      detailReviewTimeRange: [],

      summaryColumns: [
        { prop: 'partition_time', label: '处罚周期', width: 100, show: true, fixed: 'left' },
        { prop: 'ops_advertiser_name', label: '客户名称', width: 260, show: true, fixed: 'left' },
        { prop: 'zc_agent_group', label: '代理商集团', width: 160, show: true },
        { prop: 'first_level_industry_name', label: '开户一级行业', width: 160, show: true },
        { prop: 'second_level_industry_name', label: '开户二级行业', width: 180, show: true },
        { prop: 'penalty_review_total', label: '复审总量', width: 100, show: true },
        { prop: 'penalty_review_reject_cnt', label: '处罚拒绝量', width: 120, show: true },
        { prop: 'penalty_reject_rate', label: '处罚拒绝率', width: 120, show: true },
        { prop: 'is_penalized', label: '是否处罚', width: 120, show: true, fixed: 'right' }
      ],
      detailColumns: [
        { prop: 'partition_time', label: '统计周期', width: 90, show: true, fixed: 'left' },
        { prop: 'last_review_time', label: '最后审核时间', width: 170, show: true },
        { prop: 'ops_advertiser_name', label: '广告主主体名称', width: 260, show: true, fixed: 'left' },
        { prop: 'agency_uid', label: '代理商ID', width: 100, show: true },
        { prop: 'agency_name', label: '代理商名称', width: 200, show: true },
        { prop: 'zc_agent_group', label: '代理商政策集团', width: 160, show: true },
        { prop: 'account_frame', label: '账户K框', width: 120, show: true },
        { prop: 'first_level_industry_name', label: '开户一级行业', width: 150, show: true },
        { prop: 'second_level_industry_name', label: '开户二级行业', width: 170, show: true },
        { prop: 'account_id', label: 'UID', width: 120, show: true },
        { prop: 'adgroup_id', label: 'AID', width: 140, show: true },
        { prop: 'dynamic_creative_id', label: 'DCID', width: 140, show: true },
        { prop: 'component_id', label: '组件ID', width: 140, show: true },
        { prop: 'element_type', label: '元素类型', width: 140, show: true },
        { prop: 'element_value', label: '元素值', width: 260, show: true },
        { prop: 'physical_finger', label: '元素指纹', width: 200, show: true },
        { prop: 'reject_reason', label: '拒绝原因', width: 320, show: true },
        { prop: 'is_element_appeal', label: '是否有申诉记录', width: 140, show: true, fixed: 'right' }
      ]
    }
  },

  computed: {
    visibleSummaryColumns() {
      return this.summaryColumns.filter(c => c.show)
    },
    visibleDetailColumns() {
      return this.detailColumns.filter(c => c.show)
    },
    advertiserNameLocked() {
      return !!(
        this.summaryQuery.ops_advertiser_name ||
        this.detailQuery.ops_advertiser_name
      )
    },
    agentGroupLocked() {
      return !!(
        this.summaryQuery.zc_agent_group ||
        this.detailQuery.zc_agent_group
      )
    }
  },

  watch: {
    summaryColumns: {
      deep: true,
      handler() {
        this.summaryTableKey += 1
      }
    },
    detailColumns: {
      deep: true,
      handler() {
        this.detailTableKey += 1
      }
    }
  },

  mounted() {
    this.fetchSummary(true)
    this.fetchDetail(true)
  },

  methods: {
    async fetchSummary(resetPage) {
      if (resetPage) this.summaryQuery.page = 1
      this.syncSummaryTimeRange()
      this.syncLinkedFilters()
      const res = await measurementIndicator(this.cleanParams(this.summaryQuery))
      const data = res?.data || {}
      this.summaryList = data.list || []
      this.summaryTotal = data.total || 0
    },

    async fetchDetail(resetPage) {
      if (resetPage) this.detailQuery.page = 1
      this.syncDetailTimeRange()
      this.syncLinkedFilters()
      const res = await measurementDetail(this.cleanParams(this.detailQuery))
      const data = res?.data || {}
      this.detailList = data.list || []
      this.detailTotal = data.total || 0
    },

    onSummaryPageChange(page) {
      this.summaryQuery.page = page
      this.fetchSummary()
    },
    syncSummaryTimeRange() {
      const [start, end] = this.summaryPartitionRange || []
      this.summaryQuery.partition_time_start = start || ''
      this.summaryQuery.partition_time_end = end || ''
    },

    syncDetailTimeRange() {
      const [ps, pe] = this.detailPartitionRange || []
      const [rs, re] = this.detailReviewTimeRange || []

      this.detailQuery.partition_time_start = ps || ''
      this.detailQuery.partition_time_end = pe || ''
      this.detailQuery.last_review_time_start = rs || ''
      this.detailQuery.last_review_time_end = re || ''
    },

    onSummarySizeChange(size) {
      this.summaryQuery.page_size = size
      this.summaryQuery.page = 1
      this.fetchSummary()
    },

    onDetailPageChange(page) {
      this.detailQuery.page = page
      this.fetchDetail()
    },
    onDetailSizeChange(size) {
      this.detailQuery.page_size = size
      this.detailQuery.page = 1
      this.fetchDetail()
    },

    resetSummary() {
      Object.assign(this.summaryQuery, {
        partition_time: '',
        ops_advertiser_name: '',
        zc_agent_group: '',
        first_level_industry_name: '',
        second_level_industry_name: '',
        is_penalized: '',
        page: 1,
        page_size: 10
      })
      this.detailQuery.ops_advertiser_name = ''
      this.detailQuery.zc_agent_group = ''
      this.summaryPartitionRange = []
      this.fetchSummary(true)
    },

    resetDetail() {
      Object.assign(this.detailQuery, {
        partition_time: '',
        last_review_time: '',
        ops_advertiser_name: '',
        agency_uid: '',
        agency_name: '',
        zc_agent_group: '',
        account_frame: '',
        first_level_industry_name: '',
        second_level_industry_name: '',
        account_id: '',
        adgroup_id: '',
        dynamic_creative_id: '',
        component_id: '',
        element_type: '',
        element_value: '',
        physical_finger: '',
        reject_reason: '',
        is_element_appeal: '',
        page: 1,
        page_size: 10
      })
      this.summaryQuery.ops_advertiser_name = ''
      this.summaryQuery.zc_agent_group = ''
      this.detailPartitionRange = []
      this.detailReviewTimeRange = []
      this.fetchDetail(true)
    },

    cleanParams(obj) {
      const params = {}
      Object.keys(obj).forEach(k => {
        const v = obj[k]
        if (v === 0 || v === 1) {
          params[k] = v
        } else if (v !== '' && v !== null && v !== undefined) {
          params[k] = v
        }
      })
      return params
    },

    syncLinkedFilters() {
      if (this.summaryQuery.ops_advertiser_name) {
        this.detailQuery.ops_advertiser_name = this.summaryQuery.ops_advertiser_name
      } else if (this.detailQuery.ops_advertiser_name) {
        this.summaryQuery.ops_advertiser_name = this.detailQuery.ops_advertiser_name
      }

      if (this.summaryQuery.zc_agent_group) {
        this.detailQuery.zc_agent_group = this.summaryQuery.zc_agent_group
      } else if (this.detailQuery.zc_agent_group) {
        this.summaryQuery.zc_agent_group = this.detailQuery.zc_agent_group
      }
    },

    getCellCopyText(row, prop) {
      const v = row ? row[prop] : ''
      if (prop === 'element_type') {
        return this.ELEMENT_TYPE_MAP[v] || (v == null ? '' : String(v))
      }
      if (prop === 'is_penalized') {
        if (v === 1) return '是'
        if (v === 0) return '否'
      }
      if (prop === 'is_element_appeal') {
        if (v === 1) return '是'
        if (v === 0) return '否'
      }
      return v == null ? '' : String(v)
    },

    async copyCell(text, label) {
      const value = text == null ? '' : String(text)
      if (!value) return
      try {
        if (navigator.clipboard && window.isSecureContext) {
          await navigator.clipboard.writeText(value)
        } else {
          const textarea = document.createElement('textarea')
          textarea.value = value
          textarea.setAttribute('readonly', 'readonly')
          textarea.style.position = 'fixed'
          textarea.style.left = '-9999px'
          textarea.style.top = '-9999px'
          document.body.appendChild(textarea)
          textarea.select()
          document.execCommand('copy')
          document.body.removeChild(textarea)
        }
        this.$message.success(`已复制${label ? '：' + label : ''}`)
      } catch (e) {
        this.$message.error('复制失败')
      }
    }
  }
}
</script>

<style scoped>
.punish-page {
  padding: 16px;
}
.block-card {
  margin-bottom: 16px;
}
.block-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}
.block-title {
  font-size: 18px;
  font-weight: 600;
}
.filter-form {
  margin-top: 8px;
}
.cell-copy {
  cursor: pointer;
  user-select: text;
}
</style>
